import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { classPillStripSx } from "./ClassPill";

// How much of the visible strip one arrow click travels. Less than a full width
// so a pill stays on screen across the jump and the movement reads as a nudge
// rather than a page turn - the same feel as the header tab carousel.
const SCROLL_STEP_RATIO = 0.8;

// Sub-pixel rounding means scrollLeft rarely lands exactly on its bounds, so the
// end tests need a tolerance or an arrow stays enabled with nothing left to show.
const EDGE_TOLERANCE = 1;

const arrowSx = {
  flexShrink: 0,
  // Match the header tab carousel: the arrows hold their place when they run
  // out of travel (dimmed, not removed) so the strip width never jumps.
  "&.Mui-disabled": { opacity: 0.3 },
  // Desktop-only, like the tab strip's arrows: on a phone the strip is swiped,
  // and the header has no room to spare.
  display: { xs: "none", md: "inline-flex" },
};

// The horizontal class strip with carousel arrows, shared by the SBG class layout
// and the legacy Subject header so both switch classes the same way. The pills
// have always scrolled sideways, but with the scrollbar hidden nothing said so;
// these arrows give the overflow the same visible affordance the header tab strip
// has, and appear only when the pills actually overflow.
export default function ClassPillStrip({ children, sx }) {
  const scrollerRef = useRef(null);
  const [{ canScrollLeft, canScrollRight }, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const syncScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setScrollState((prev) => {
      const next = {
        canScrollLeft: el.scrollLeft > EDGE_TOLERANCE,
        canScrollRight: el.scrollLeft < maxScroll - EDGE_TOLERANCE,
      };
      // Scroll fires far more often than the arrows change, so bail on a no-op
      // rather than re-rendering the whole strip on every frame of a swipe.
      if (
        prev.canScrollLeft === next.canScrollLeft &&
        prev.canScrollRight === next.canScrollRight
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  // Overflow depends on both the available width and the pills themselves, so
  // watch the scroller (viewport changes) and its content (classes arriving or
  // the term filter shortening the list) rather than a single render pass.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    syncScrollState();

    const observer = new ResizeObserver(syncScrollState);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [syncScrollState, children]);

  const scrollBy = (direction) => () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * el.clientWidth * SCROLL_STEP_RATIO,
      behavior: "smooth",
    });
  };

  // Both arrows are mounted whenever either direction has travel: an overflowing
  // strip keeps a stable width, and a strip that fits shows no arrows at all.
  const overflowing = canScrollLeft || canScrollRight;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        ...sx,
      }}
    >
      {overflowing && (
        <IconButton
          size="small"
          aria-label="Scroll classes left"
          onClick={scrollBy(-1)}
          disabled={!canScrollLeft}
          sx={arrowSx}
        >
          <KeyboardArrowLeftIcon fontSize="small" />
        </IconButton>
      )}
      <Box ref={scrollerRef} sx={classPillStripSx} onScroll={syncScrollState}>
        {children}
      </Box>
      {overflowing && (
        <IconButton
          size="small"
          aria-label="Scroll classes right"
          onClick={scrollBy(1)}
          disabled={!canScrollRight}
          sx={arrowSx}
        >
          <KeyboardArrowRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
